import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConsoleRole } from '../../session/SessionContext';
import { GUIDE } from '../../content/guideData';
import { guideNames, guideOf, guideRoleFor, mayPickGuide } from './guideVals';
import { GuidePicker } from './GuidePicker';
import { GuideStaff } from './GuideStaff';
import { GuideBig } from './GuideBig';
import './guide.css';

/**
 * The Guide (/guide), ported from the design's Guide page (Aithora Guide.dc.html with
 * guide-data.js). One guide per role, and the role comes ONLY from the signed-in session:
 * a Care Staff member reading /guide gets the Care Staff guide whatever the URL says.
 * Super Admin and Dev (who read the Operator's guide) also get the design's role picker
 * and its cross-role search, and may open another role's guide with ?for=<guide>; the
 * URL is ignored for everyone else. ?topic=<slug> opens a step or an answer.
 */
/**
 * True after focus was last moved with the keyboard (Tab), false after a pointer press.
 * The picker's search box draws its focus ring only then: a text field always matches
 * :focus-visible, so the browser cannot tell a click from a Tab by itself.
 */
export function useKeyboardFocus(): boolean {
  const [kbd, setKbd] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') setKbd(true);
    };
    const onPointer = () => setKbd(false);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointer, true);
    };
  }, []);
  return kbd;
}

export function GuidePage() {
  const { role } = useConsoleRole();
  const kbd = useKeyboardFocus();
  const [params] = useSearchParams();
  if (!GUIDE) {
    return (
      <div className="guide" style={{ padding: 28 }}>
        The guide&apos;s content did not load.
      </div>
    );
  }
  const own = guideRoleFor(role);
  const picker = mayPickGuide(role);
  const asked = params.get('for');
  // Super Admin and Dev land on the picker; everyone else on their own guide.
  const raw = picker ? (asked && guideNames(GUIDE).includes(asked) ? asked : null) : own;
  const g = raw ? guideOf(GUIDE, raw) : null;
  return (
    <div className="guide" data-kbd={kbd ? 'true' : undefined}>
      {!g || !g.R ? (
        <GuidePicker G={GUIDE} own={own} />
      ) : g.R.big ? (
        <GuideBig key={g.raw} G={GUIDE} raw={g.raw} name={g.name} R={g.R} />
      ) : (
        <GuideStaff key={g.raw} raw={g.raw} name={g.name} R={g.R} />
      )}
    </div>
  );
}
